import { useId, useState } from 'react';
import { apiFetch } from '~/lib/api';
import TermsCheckbox from '~/components/TermsCheckbox';
import { useT } from '~/i18n';
import { TBIT_JOBS_TERMS_VERSION, readTermsError, termsAcceptanceBody } from '~/lib/termsVersions';

/**
 * La casilla APARTE del perfil público: «Entiendo que mi perfil va a ser
 * público…». Va sin marcar y no se mezcla con los términos (la Ley 1581 y el
 * RGPD no aceptan el consentimiento metido dentro de otro ni casillas
 * premarcadas). La usan el alta y «Quién te encuentra».
 */
export function PublicAckCheckbox({
  id,
  checked,
  onChange,
  error,
  disabled,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: boolean;
  disabled?: boolean;
}) {
  const t = useT('onboarding').consent.ack;
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          id={id}
          type="checkbox"
          className="checkbox checkbox-primary checkbox-sm mt-0.5"
          checked={checked}
          disabled={disabled}
          aria-required
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>
          {t.before}
          <b>{t.strong}</b>
          {t.after}
        </span>
      </label>
      {error && (
        <p id={`${id}-error`} className="text-xs" style={{ color: 'var(--coral-ink)' }} role="alert">
          {t.required}
        </p>
      )}
    </div>
  );
}

/** Lo que el perfil público muestra y lo que nunca muestra. */
export function PublicSeenBox() {
  const t = useT('onboarding').consent;
  return (
    <div className="grid grid-cols-1 gap-2 text-sm">
      <div className="p-3 rounded-2xl" style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}>
        <p className="font-medium">{t.seenTitle}</p>
        <p className="hush">{t.seenBody}</p>
      </div>
      <div className="p-3 rounded-2xl" style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}>
        <p className="font-medium">{t.neverTitle}</p>
        <p className="hush">{t.neverBody}</p>
      </div>
    </div>
  );
}

type Choice = 'public' | 'private';

/**
 * El último paso del alta (y, una sola vez, la compuerta de las cuentas que ya
 * existían): términos + quién puede ver el perfil. «Público» viene
 * preseleccionado; para seguir con él hay que marcar la casilla aparte, y
 * «Privado» se elige en la misma pantalla sin castigo.
 *
 * `onClose` solo lo pasan las cuentas que ya existían: cerrar no escribe nada
 * y el perfil queda privado.
 */
export default function ConsentStep({
  freeTier,
  onDone,
  onClose,
}: {
  freeTier: boolean;
  onDone: () => void;
  onClose?: () => void;
}) {
  const t = useT('onboarding').consent;
  const uid = useId();
  const [choice, setChoice] = useState<Choice>('public');
  const [ack, setAck] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsVersion, setTermsVersion] = useState(TBIT_JOBS_TERMS_VERSION);
  const [ackProblem, setAckProblem] = useState(false);
  const [termsProblem, setTermsProblem] = useState<'required' | 'outdated' | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    const needAck = choice === 'public' && !ack;
    const needTerms = !termsAccepted;
    setAckProblem(needAck);
    setTermsProblem(needTerms ? 'required' : null);
    if (needAck || needTerms) return;

    setSaving(true);
    try {
      const res = await apiFetch('/job-apply/api/consent', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...termsAcceptanceBody(termsVersion),
          public_profile: choice,
          ...(choice === 'public' ? { public_ack: true } : {}),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        onDone();
        return;
      }
      const termsError = readTermsError(res.status, body);
      if (termsError) {
        if (termsError.current_version) setTermsVersion(termsError.current_version);
        setTermsAccepted(false);
        setTermsProblem(termsError.code === 'terms_outdated' ? 'outdated' : 'required');
        return;
      }
      if (body?.code === 'public_ack_required') {
        setAck(false);
        setAckProblem(true);
        return;
      }
      setError(t.saveFailed);
    } catch {
      setError(t.connectionError);
    } finally {
      setSaving(false);
    }
  };

  const option = (value: Choice, label: string, hint: string, badge?: string) => (
    <label
      className="flex items-start gap-3 p-3 rounded-2xl cursor-pointer"
      style={{
        border: `1px solid ${choice === value ? 'var(--purple-ink)' : 'var(--hairline)'}`,
        background: 'var(--surface-hi)',
      }}
    >
      <input
        type="radio"
        name={`${uid}-vis`}
        className="radio radio-primary radio-sm mt-1"
        checked={choice === value}
        onChange={() => {
          setChoice(value);
          // Volver a Público exige consentir de nuevo, activamente.
          setAck(false);
          setAckProblem(false);
        }}
      />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold">
          {label}
          {badge && <span className="badge badge-info badge-sm ml-2">{badge}</span>}
        </span>
        <span className="text-xs hush">{hint}</span>
      </span>
    </label>
  );

  return (
    <div className="flex flex-col items-center py-8">
      <div className="card shadow-lg max-w-md w-full rise">
        <div className="card-body space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs uppercase tracking-wide hush">{t.step}</p>
              <h2 className="card-title text-xl">{t.title}</h2>
            </div>
            {onClose && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
                {t.close}
              </button>
            )}
          </div>
          <p className="text-sm hush">{t.lede}</p>

          <div role="radiogroup" aria-label={t.groupLabel} className="space-y-2">
            {option('public', t.publicLabel, t.publicHint, t.recommended)}
            {option('private', t.privateLabel, t.privateHint)}
          </div>

          <PublicSeenBox />

          {choice === 'public' && (
            <PublicAckCheckbox
              id={`${uid}-ack`}
              checked={ack}
              onChange={(v) => {
                setAck(v);
                if (v) setAckProblem(false);
              }}
              error={ackProblem}
            />
          )}

          <TermsCheckbox
            id={`${uid}-terms`}
            checked={termsAccepted}
            onChange={(v) => {
              setTermsAccepted(v);
              if (v) setTermsProblem(null);
            }}
            before={t.terms.before}
            label={t.terms.label}
            href={t.terms.href}
            after={t.terms.after}
            notice={termsProblem === 'outdated' ? t.terms.outdated : null}
            error={termsProblem === 'required' ? t.terms.required : null}
          />

          {error && (
            <p className="text-sm" style={{ color: 'var(--coral-ink)' }} role="alert">
              {error}
            </p>
          )}

          <button type="button" className="btn btn-primary w-full" onClick={submit} disabled={saving}>
            {saving && <span className="loading loading-spinner loading-sm" />}
            {saving ? t.saving : t.cta}
          </button>
          {freeTier && <p className="text-xs hush text-center">{t.noCard}</p>}
          {onClose && <p className="text-xs hush text-center">{t.closeNote}</p>}
        </div>
      </div>
    </div>
  );
}
